import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ApiErrorHandlerService } from "../../utils/api-error-handler/api-error-handler.service";
import { Observable } from "rxjs";
import { DOCUMENT } from "@angular/common";
import { SysAlert } from "../../../models/api/sysAlert";

@Injectable({
  providedIn: "root",
})
export class SystemAlertService {
  private http = inject(HttpClient);
  private apiErrorHandler = inject(ApiErrorHandlerService);
  private document = inject(DOCUMENT);
  private localStorage = this.document.defaultView?.localStorage;

  /**
   * Reset all read status.
   * This will make all alerts be displayed again.
   */
  public resetReadStatus() {
    this.localStorage?.removeItem("readAlerts");
  }

  /**
   * Marks the specified alert as read by adding its ID to the read alerts list in local storage.
   * @param alert The system alert to mark as read.
   */
  public readAlert(alert: SysAlert) {
    const readAlerts = JSON.parse(this.localStorage?.getItem("readAlerts") || "[]");
    readAlerts.push(alert.id);
    this.localStorage?.setItem("readAlerts", JSON.stringify(readAlerts));
  }

  /**
   * Returns whether the specified alert has been marked as read.
   * @param alert The system alert to check.
   * @returns Whether the alert has been marked as read.
   */
  public getReadStatus(alert: SysAlert) {
    const readAlerts = JSON.parse(this.localStorage?.getItem("readAlerts") || "[]");
    return readAlerts.includes(alert.id);
  }

  /**
   * Fetches system alerts from the server.
   * @param active If true, fetches only active alerts; otherwise, fetches all alerts.
   * @returns An observable of the list of system alerts.
   */
  public getAlerts(active: boolean): Observable<SysAlert[]> {
    if (active) {
      return this.http
        .get<SysAlert[]>(`${environment.baseUrl}/systemAlert/active`)
        .pipe(this.apiErrorHandler.handle("system_alert"));
    } else {
      return this.http
        .get<SysAlert[]>(`${environment.baseUrl}/systemAlert`)
        .pipe(this.apiErrorHandler.handle("system_alert"));
    }
  }

  /**
   * Creates a new system alert.
   * Sends a POST request to the server to add the system alert.
   * @param alert The system alert to create.
   * @returns An observable of the created system alert.
   */
  public createAlert(alert: SysAlert): Observable<SysAlert> {
    return this.http
      .post<SysAlert>(`${environment.baseUrl}/systemAlert`, alert)
      .pipe(this.apiErrorHandler.handle("system_alert"));
  }

  /**
   * Updates an existing system alert.
   * Sends a PUT request to the server to update the system alert information.
   * @param alert The system alert to update. The alert's ID is used to identify the alert.
   * @returns An observable of the updated system alert.
   */
  public editAlert(alert: SysAlert): Observable<SysAlert> {
    const alertId = alert.id;
    delete alert.id;
    return this.http
      .put<SysAlert>(`${environment.baseUrl}/systemAlert/${alertId}`, alert)
      .pipe(this.apiErrorHandler.handle("system_alert"));
  }

  /**
   * Deletes a system alert.
   * Sends a DELETE request to the server to remove the system alert.
   * @param alert The system alert to delete. The alert's ID is used to identify the alert.
   * @returns An observable of the HTTP response.
   */
  public deleteAlert(alert: SysAlert) {
    const alertId = alert.id;
    return this.http
      .delete(`${environment.baseUrl}/systemAlert/${alertId}`)
      .pipe(this.apiErrorHandler.handle("system_alert"));
  }
}
